
import { supabase } from '@/integrations/supabase/client';

export class CSRFService {
  private static instance: CSRFService;
  
  static getInstance(): CSRFService {
    if (!CSRFService.instance) {
      CSRFService.instance = new CSRFService();
    }
    return CSRFService.instance;
  }

  /**
   * Generate a new CSRF token from the server
   */
  async generateToken(userSessionId?: string): Promise<string | null> {
    try {
      console.log('CSRFService: Generating new CSRF token');
      
      const { data, error } = await supabase.functions.invoke('csrf-manager', {
        body: { 
          action: 'generate', 
          userSessionId 
        }
      });

      if (error) {
        console.error('CSRFService: Error generating token:', error);
        return null;
      }

      // Extract token from response data
      const token = data?.token;
      
      if (!token) {
        console.error('CSRFService: No token received in response');
        return null;
      }

      console.log('CSRFService: Token generated successfully');
      return token;
    } catch (error) {
      console.error('CSRFService: Token generation failed:', error);
      return null;
    }
  }

  /**
   * Validate a CSRF token on the server
   */
  async validateToken(token: string, userSessionId?: string): Promise<boolean> {
    try {
      console.log('CSRFService: Validating CSRF token');
      
      const { data, error } = await supabase.functions.invoke('csrf-manager', {
        body: { 
          action: 'validate', 
          token, 
          userSessionId 
        }
      });

      if (error) {
        console.error('CSRFService: Error validating token:', error);
        return false;
      }

      const result = data as { valid: boolean };
      console.log('CSRFService: Token validation result:', result.valid);
      return result.valid;
    } catch (error) {
      console.error('CSRFService: Token validation failed:', error);
      return false;
    }
  }

  /**
   * Add CSRF token to request headers
   */
  async addToRequest(init: RequestInit = {}, userSessionId?: string): Promise<RequestInit> {
    const token = await this.generateToken(userSessionId);
    
    if (!token) {
      console.warn('CSRFService: Could not generate token for request');
      return init;
    }

    return {
      ...init,
      headers: {
        ...init.headers,
        'X-CSRF-Token': token,
        'Content-Type': 'application/json'
      }
    };
  }

  /**
   * Get user session ID for CSRF token binding
   */
  async getUserSessionId(): Promise<string | null> {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error || !session) {
        return null;
      }

      // Use session access token as session identifier
      return session.access_token.substring(0, 32); // First 32 chars as session ID
    } catch (error) {
      console.error('CSRFService: Error getting session ID:', error);
      return null;
    }
  }
}

export const csrfService = CSRFService.getInstance();
